import React, { useState, useEffect } from 'react';
import '../styles/CommentsSection.css';

const CommentsSection = ({ postId, user, token, isAuthenticated, apiUrl }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [commentLikes, setCommentLikes] = useState({});
  const [likingComments, setLikingComments] = useState(new Set());
  const [deletingComment, setDeletingComment] = useState(null);

  useEffect(() => {
    if (postId) fetchComments();
  }, [postId]);

  useEffect(() => {
    if (comments.length > 0 && isAuthenticated && user?.id) {
      fetchCommentsLikes();
    }
  }, [comments, isAuthenticated, user?.id]);

  const fetchComments = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/v1/comentario/publicacion/${postId}`);
      if (response.ok) {
        const data = await response.json();
        setComments(Array.isArray(data) ? data : []);
      } else {
        setComments([]);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      setComments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommentsLikes = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/v1/like/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const allLikes = await response.json();
        const commentLikesData = {};

        comments.forEach(comment => {
          const commentLikesArray = allLikes.filter(
            like =>
              like.id_comentario === comment.id_comentario &&
              like.id_publicacion === parseInt(postId)
          );

          commentLikesData[comment.id_comentario] = {
            count: commentLikesArray.length,
            isLiked: commentLikesArray.some(like => like.id_usuario === user.id)
          };
        });

        setCommentLikes(commentLikesData);
      }
    } catch (error) {
      console.error('Error fetching comment likes:', error);
    }
  };

  const handleSubmitComment = async () => {
    if (!isAuthenticated || !user?.id) {
      alert('Debes iniciar sesión para comentar');
      return;
    }
    if (!newComment.trim()) {
      alert('El comentario no puede estar vacío');
      return;
    }

    setSubmitting(true);
    try {
      const commentData = {
        descripcion_comentario: newComment.trim(),
        id_usuario: user.id,
        id_publicacion: parseInt(postId)
      };

      const response = await fetch(`${apiUrl}/api/v1/comentario/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(commentData)
      });

      if (response.ok) {
        setNewComment('');
        await fetchComments();
      } else {
        alert('Error al crear el comentario');
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
      alert('Error de conexión al enviar el comentario');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCommentLike = async (commentId) => {
    if (!isAuthenticated || !user?.id) {
      alert('Debes iniciar sesión para dar like');
      return;
    }
    if (likingComments.has(commentId)) return;

    setLikingComments(prev => new Set([...prev, commentId]));

    try {
      const currentCommentLike = commentLikes[commentId];
      const isCurrentlyLiked = currentCommentLike?.isLiked || false;

      if (isCurrentlyLiked) {
        const likeData = {
          id_usuario: user.id,
          id_publicacion: parseInt(postId),
          id_comentario: commentId
        };

        const response = await fetch(`${apiUrl}/api/v1/like/`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(likeData)
        });

        if (response.ok) {
          setCommentLikes(prev => ({
            ...prev,
            [commentId]: {
              count: Math.max(0, (prev[commentId]?.count || 0) - 1),
              isLiked: false
            }
          }));
        }
      } else {
        const likeData = {
          id_usuario: user.id,
          id_publicacion: parseInt(postId),
          id_comentario: commentId
        };

        const response = await fetch(`${apiUrl}/api/v1/like/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(likeData)
        });

        if (response.ok) {
          setCommentLikes(prev => ({
            ...prev,
            [commentId]: {
              count: (prev[commentId]?.count || 0) + 1,
              isLiked: true
            }
          }));
        }
      }
    } catch (error) {
      console.error('Error handling comment like:', error);
    } finally {
      setLikingComments(prev => {
        const newSet = new Set(prev);
        newSet.delete(commentId);
        return newSet;
      });
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('¿Seguro que quieres eliminar este comentario?')) return;

    setDeletingComment(commentId);
    try {
      const response = await fetch(`${apiUrl}/api/v1/comentario/${commentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setComments(prev => prev.filter(c => c.id_comentario !== commentId));
      } else {
        alert('Error al eliminar comentario');
      }
    } catch (error) {
      console.error('Error eliminando comentario:', error);
      alert('Error de conexión al eliminar');
    } finally {
      setDeletingComment(null);
    }
  };

  if (loading) {
    return (
      <div className="comments-section">
        <h3>Comentarios</h3>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
          Cargando comentarios...
        </div>
      </div>
    );
  }

  return (
    <div className="comments-section">
      <h3>Comentarios ({comments.length})</h3>

      {isAuthenticated ? (
        <div className="comment-form">
          <div className="comment-input-container">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Escribe un comentario..."
              className="comment-input"
              rows="3"
              disabled={submitting}
            />
          </div>
          <div className="comment-form-actions">
            <button
              onClick={handleSubmitComment}
              disabled={submitting || !newComment.trim()}
              className="submit-comment-btn"
            >
              {submitting ? 'Enviando...' : 'Comentar'}
            </button>
          </div>
        </div>
      ) : (
        <div className="login-prompt">
          <p>Inicia sesión para poder comentar</p>
        </div>
      )}

      <div className="comments-list">
        {comments.length === 0 ? (
          <div className="no-comments">
            <p>Aún no hay comentarios. ¡Sé el primero en comentar!</p>
          </div>
        ) : (
          comments.map((comment) => (
            <div key={comment.id_comentario} className="comment-item">
              <div className="comment-header">
                <div className="comment-user-info">
                  <span className="comment-username">
                    {comment.nombre_usuario || `Usuario ${comment.id_usuario}`}
                  </span>
                </div>

                {/* Botón eliminar en la esquina superior derecha */}
                {isAuthenticated && user?.id === comment.id_usuario && (
                  <button
                    onClick={() => handleDeleteComment(comment.id_comentario)}
                    disabled={deletingComment === comment.id_comentario}
                    className="delete-comment-btn"
                    title="Eliminar comentario"
                  >
                    {deletingComment === comment.id_comentario ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="loading-icon"
                      >
                        <circle cx="12" cy="12" r="10"/>
                        <path d="m9 12 2 2 4-4"/>
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="3,6 5,6 21,6"/>
                        <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"/>
                        <line x1="10" y1="11" x2="10" y2="17"/>
                        <line x1="14" y1="11" x2="14" y2="17"/>
                      </svg>
                    )}
                  </button>
                )}
              </div>

              <div className="comment-content">
                <p>{comment.descripcion_comentario}</p>
              </div>

              <div className="comment-actions">
                <button
                  onClick={() => handleCommentLike(comment.id_comentario)}
                  disabled={!isAuthenticated || likingComments.has(comment.id_comentario)}
                  className={`comment-like-btn ${commentLikes[comment.id_comentario]?.isLiked ? 'liked' : ''}`}
                  title={!isAuthenticated ? 'Inicia sesión para dar like' : ''}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill={commentLikes[comment.id_comentario]?.isLiked ? "#ff3040" : "none"}
                    stroke={commentLikes[comment.id_comentario]?.isLiked ? "#ff3040" : (!isAuthenticated ? "#ccc" : "#666")}
                    strokeWidth="2"
                  >
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                  {commentLikes[comment.id_comentario]?.count > 0 && (
                    <span className="like-count">{commentLikes[comment.id_comentario].count}</span>
                  )}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CommentsSection;
